import classnames from 'classnames'
import { FieldValidator, useField } from 'formik'
import React, { ComponentPropsWithoutRef, useCallback, useId, useMemo, useRef, useState } from 'react'
import { mergeRefs } from 'react-merge-refs'

import ExpandedIndicator from '../ExpandedIndicator/ExpandedIndicator'
import Field from '../Field/Field'
import FieldDropdown from '../FieldDropdown/FieldDropdown'
import FieldError from '../FieldError/FieldError'

import styles from './Select.module.scss'

const nativeSelectValueSetter = (Object as any).getOwnPropertyDescriptor(
  window.HTMLSelectElement.prototype,
  'value'
).set

type SelectProps = {
  label: string
  value?: string
  options: { value: string; label: React.ReactNode }[]

  omitErrorDisplay?: boolean
  hideLabel?: boolean

  touched?: boolean
  error?: string
} & Pick<React.SelectHTMLAttributes<HTMLSelectElement>, 'name' | 'required' | 'disabled' | 'onChange' | 'onBlur'>

/**********************************************************************
 * UseSelect
 **********************************************************************/
const useSelect = ({ value, options, error, name, required, disabled, onChange, onBlur }: SelectProps, ref: any) => {
  const listboxId = useId()

  const selectRef = useRef<HTMLSelectElement>(null)
  const listboxRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLDivElement>(null)

  const [open, setOpen] = useState(false)

  const currentValue = value
  const currentIndex = useMemo(
    () => options.findIndex((option) => option.value === currentValue),
    [options, currentValue]
  )

  const [, setIteration] = useState(0)
  const handleChange = useCallback<React.ChangeEventHandler<HTMLSelectElement>>(
    (e) => {
      onChange?.(e)
      setTimeout(() => setIteration((prev) => prev + 1), 0)
    },
    [onChange, setIteration]
  )

  const focusCurrentItem = useCallback(() => {
    setTimeout(() => {
      const value = selectRef.current?.value
      let el = listboxRef.current?.querySelector<HTMLDivElement>(`[data-value="${value}"]`)
      if (!el) {
        el = listboxRef.current?.querySelector<HTMLDivElement>(`[data-value]`)
      }
      if (!el) {
        el = listboxRef.current
      }
      el?.focus()
    }, 0)
  }, [selectRef, listboxRef])

  const setValue = useCallback(
    (value: string) => {
      const input = selectRef.current
      nativeSelectValueSetter.call(input, value)
      input?.dispatchEvent(new Event('change', { bubbles: true }))
    },
    [selectRef]
  )

  const handleComboboxKeydown = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(
    (e) => {
      if (
        e.code === 'ArrowDown' ||
        e.code === 'ArrowUp' ||
        e.code === 'PageUp' ||
        e.code === 'PageDown' ||
        e.code === 'Home' ||
        e.code === 'End'
      ) {
        e.preventDefault()

        const currentValue = selectRef.current?.value

        let idx = options.findIndex((option) => option.value === currentValue)
        if (e.code === 'ArrowDown') {
          idx = Math.min(idx + 1, options.length - 1)
        } else if (e.code === 'ArrowUp') {
          idx = Math.max(idx - 1, 0)
        } else if (['PageUp', 'Home'].includes(e.code)) {
          idx = 0
        } else if (['PageDown', 'End'].includes(e.code)) {
          idx = options.length - 1
        }

        if (options[idx]) {
          setValue(options[idx].value)
        }
      } else if (['Space', 'Return', 'Enter'].includes(e.code)) {
        e.preventDefault()
        setOpen(() => true)
        focusCurrentItem()
      }
    },
    [setValue, options, focusCurrentItem]
  )

  const handleComboboxMousedown = useCallback<React.MouseEventHandler<HTMLDivElement>>(() => {
    setOpen((open) => {
      if (!open) {
        focusCurrentItem()
        return true
      }
      return false
    })
  }, [setOpen, focusCurrentItem])

  const handleBlur = useCallback<React.FocusEventHandler>(
    (e) => {
      const input = selectRef.current
      if (e.target === input) {
        return
      }
      e.stopPropagation()
      e.preventDefault()
      input?.dispatchEvent(new FocusEvent('focusout', { bubbles: true }))
    },
    [selectRef]
  )

  const handleListboxKeydown = useCallback<React.KeyboardEventHandler<HTMLDivElement>>(
    (e) => {
      if (
        e.code === 'ArrowDown' ||
        e.code === 'ArrowUp' ||
        e.code === 'PageUp' ||
        e.code === 'PageDown' ||
        e.code === 'Home' ||
        e.code === 'End'
      ) {
        e.preventDefault()

        const currentValue = selectRef.current?.value

        let idx = options.findIndex((option) => option.value === currentValue)
        if (e.code === 'ArrowDown') {
          idx = Math.min(idx + 1, options.length - 1)
        } else if (e.code === 'ArrowUp') {
          idx = Math.max(idx - 1, 0)
        } else if (['PageUp', 'Home'].includes(e.code)) {
          idx = 0
        } else if (['PageDown', 'End'].includes(e.code)) {
          idx = options.length - 1
        }

        if (options[idx]) {
          setValue(options[idx].value)
        }
        focusCurrentItem()
      } else if (['Tab', 'Escape', 'Return', 'Enter'].includes(e.code)) {
        e.preventDefault()
        setOpen(false)
        triggerRef.current?.focus()
      } else if (e.code === 'Space') {
        e.preventDefault()
      }
    },
    [options, selectRef, setValue, focusCurrentItem, triggerRef, setOpen]
  )

  const handleListboxClick = useCallback<React.MouseEventHandler<HTMLDivElement>>(
    (e) => {
      const value = (e.target as any).dataset.value
      if (value !== undefined) {
        setValue(value)
        setOpen(false)
        triggerRef.current?.focus()
      }
    },
    [setOpen, setValue, triggerRef]
  )

  const handleListboxBlur = useCallback<React.FocusEventHandler<HTMLDivElement>>(
    (e) => {
      if (!listboxRef.current!.contains(e.relatedTarget)) {
        setOpen(false)
      }
    },
    [setOpen, listboxRef]
  )

  return {
    wrapperProps: {
      onBlur: handleBlur,
    },
    nativeSelect: (
      <select
        ref={mergeRefs([selectRef, ref])}
        name={name}
        required={required}
        disabled={disabled}
        value={value}
        aria-hidden="true"
        tabIndex={-1}
        onChange={handleChange}
        onBlur={onBlur as any}
      >
        {options.map((option, idx) => (
          <option key={option.value} value={option.value}>
            {idx}
          </option>
        ))}
      </select>
    ),
    triggerProps: {
      ref: triggerRef,
      onKeyDown: handleComboboxKeydown,
      onMouseDown: handleComboboxMousedown,
      tabIndex: 0,
      role: 'combobox',
      'aria-expanded': open,
      'aria-required': required,
      'data-focus-input': name,
      'aria-controls': open ? listboxId : undefined,
      'aria-invalid': Boolean(error),
      'aria-disabled': disabled,
    },
    listboxProps: {
      id: listboxId,
      ref: listboxRef,
      role: 'listbox',
      tabIndex: -1,
      onKeyDown: handleListboxKeydown,
      onClick: handleListboxClick,
      onBlur: handleListboxBlur,
      children: options.map((option) => (
        <div
          tabIndex={-1}
          key={option.value}
          data-value={option.value}
          role="option"
          aria-selected={option.value === currentValue}
        >
          {option.label}
        </div>
      )),
    },
    currentIndex,
    open,
  }
}

/**********************************************************************
 * Select
 **********************************************************************/
const Select = React.memo(
  React.forwardRef(function Select(
    {
      value,
      label,
      options,
      omitErrorDisplay,
      hideLabel,
      touched,
      error,
      name,
      required,
      disabled,
      onChange,
      onBlur,
      className,
    }: SelectProps & { className?: string },
    ref: any
  ) {
    const errorMessageId = useId()

    const { wrapperProps, nativeSelect, triggerProps, listboxProps, currentIndex, open } = useSelect(
      { value, label, options, touched, error, name, required, disabled, onChange, onBlur },
      ref
    )

    return (
      <div className={classnames(styles.Select, className)} {...wrapperProps}>
        <div className={styles.nativeinput}>{nativeSelect}</div>
        <Field
          label={hideLabel ? undefined : label}
          hasError={(touched && triggerProps['aria-invalid']) || undefined}
          disabled={triggerProps['aria-disabled']}
          aria-label={label}
          aria-describedby={errorMessageId}
          {...triggerProps}
          className={styles.field}
        >
          <span className={styles.value}>{options[currentIndex]?.label}</span>
          <ExpandedIndicator />
        </Field>
        <FieldDropdown open={open} anchor={triggerProps.ref.current} className={styles.dropdown} {...listboxProps} />
        <FieldError error={error} touched={touched} omitErrorDisplay={omitErrorDisplay} id={errorMessageId} />
      </div>
    )
  })
)

export default Select

/**********************************************************************
 * SelectFormik
 **********************************************************************/
type SelectFormikProps = {
  name: string
  validate?: FieldValidator
} & Omit<ComponentPropsWithoutRef<typeof Select>, 'value'>

export const SelectFormik = React.memo(function SelectFormik({ name, validate, ...componentProps }: SelectFormikProps) {
  const inputRef = React.useRef<HTMLInputElement>(null)

  const doValidate = useCallback(
    (value?: any) => {
      if (!inputRef.current?.validity.valid) {
        return inputRef.current?.validationMessage
      }
      if (validate) {
        return validate(value)
      }
    },
    [inputRef, validate]
  )

  const [field, meta, _helpers] = useField({
    name,
    validate: doValidate,
    ...componentProps,
  })

  return <Select ref={inputRef} touched={meta.touched} error={meta.error} {...componentProps} {...field} />
})
